/**
 * 权限管理模块
 * 基于数据库权限的细粒度权限控制
 * P0修复: 权限数据仅存储在内存中，不持久化到localStorage，防止客户端篡改
 */

import { create } from 'zustand';
import { hasFullAccessRole } from '../utils/fullAccessRole';

export type PermissionCode = string;

interface PermissionState {
  permissions: PermissionCode[];
  role: string;
  roles: string[];
  loaded: boolean;
  sessionId: string | null;

  setPermissions: (permissions: PermissionCode[]) => void;
  setRole: (role: string) => void;
  setRoles: (roles: string[]) => void;
  setSessionId: (sessionId: string) => void;
  hasPermission: (permission: PermissionCode) => boolean;
  hasAnyPermission: (permissions: PermissionCode[]) => boolean;
  hasAllPermissions: (permissions: PermissionCode[]) => boolean;
  clearPermissions: () => void;
}

export const usePermissionStore = create<PermissionState>()(
  (set, get) => ({
    permissions: [],
    role: '',
    roles: [],
    loaded: false,
    sessionId: null,

    setPermissions: (permissions) => set({ permissions, loaded: true }),

    setRole: (role) => set({ role }),

    setRoles: (roles) => set({ roles: roles ?? [] }),

    setSessionId: (sessionId) => set({ sessionId }),

    hasPermission: (permission) => {
      const { permissions, role, roles } = get();
      if (hasFullAccessRole(role, roles)) return true;
      return permissions.includes(permission);
    },

    hasAnyPermission: (permissionList) => {
      const { permissions, role, roles } = get();
      if (hasFullAccessRole(role, roles)) return true;
      return permissionList.some(p => permissions.includes(p));
    },

    hasAllPermissions: (permissionList) => {
      const { permissions, role, roles } = get();
      if (hasFullAccessRole(role, roles)) return true;
      return permissionList.every(p => permissions.includes(p));
    },

    clearPermissions: () => set({ permissions: [], role: '', roles: [], loaded: false, sessionId: null }),
  })
);

export const PERMISSIONS = {
  CRM: {
    COMPANY: {
      LIST: 'crm.company.list',
      GET: 'crm.company.get',
      ADD: 'crm.company.add',
      EDIT: 'crm.company.edit',
      REMOVE: 'crm.company.remove',
      EXPORT: 'crm.company.export',
      ASSIGN: 'crm.company.assign',
      PUBLIC_POOL_VIEW: 'crm.company.public-pool.view',
      PUBLIC_POOL_MANAGE: 'crm.company.public-pool.manage',
      PUBLIC_POOL_CLAIM: 'crm.company.public-pool.claim',
    },
    OPPORTUNITY: {
      LIST: 'crm.opportunity.list',
      GET: 'crm.opportunity.get',
      ADD: 'crm.opportunity.add',
      EDIT: 'crm.opportunity.edit',
      REMOVE: 'crm.opportunity.remove',
      SIGN: 'crm.opportunity.sign',
      ASSIGN: 'crm.opportunity.assign',
    },
    ACTIVITY: {
      /** 全公司范围（管理员/特权角色） */
      VIEW_ALL: 'crm.activity.view_all',
      /** 仅本人负责活动 */
      SCOPE_SELF: 'crm.activity.scope.self',
      /** 本人 + 本部门活动 */
      SCOPE_DEPT: 'crm.activity.scope.dept',
      LIST: 'crm.activity.list',
      GET: 'crm.activity.get',
      ADD: 'crm.activity.add',
      EDIT: 'crm.activity.edit',
      REMOVE: 'crm.activity.remove',
      PARTICIPANT_EDIT: 'crm.activity.participant.edit',
      CONVERT: 'crm.activity.convert',
      IMPORT: 'crm.activity.import',
      EXPORT: 'crm.activity.export',
      CANCEL: 'crm.activity.cancel',
      START: 'crm.activity.start',
      OPPORTUNITY_LIST: 'crm.activity.opportunity.list',
    },
    PROJECT: {
      LIST: 'crm.project.list',
      GET: 'crm.project.get',
      ADD: 'crm.project.add',
      EDIT: 'crm.project.edit',
      REMOVE: 'crm.project.remove',
    },
    TASK: {
      LIST: 'crm.task.list',
      GET: 'crm.task.get',
      ADD: 'crm.task.add',
      EDIT: 'crm.task.edit',
      REMOVE: 'crm.task.remove',
    },
    CONTACT: {
      LIST: 'crm.contact.list',
      GET: 'crm.contact.get',
      ADD: 'crm.contact.add',
      EDIT: 'crm.contact.edit',
      REMOVE: 'crm.contact.remove',
    },
    LEAD: {
      LIST: 'crm.lead.list',
      GET: 'crm.lead.get',
      CREATE: 'crm.lead.create',
      UPDATE: 'crm.lead.update',
      DELETE: 'crm.lead.delete',
      ASSIGN: 'crm.lead.assign',
      INVALID: 'crm.lead.invalid',
      CONVERT: 'crm.lead.convert',
    },
    FOLLOW: {
      LIST: 'crm.follow.list',
      GET: 'crm.follow.get',
      ADD: 'crm.follow.add',
      EDIT: 'crm.follow.edit',
      REMOVE: 'crm.follow.remove',
    },
    SCHEDULE: {
      LIST: 'crm.schedule.list',
      GET: 'crm.schedule.get',
      ADD: 'crm.schedule.add',
      EDIT: 'crm.schedule.edit',
      REMOVE: 'crm.schedule.remove',
    },
    TAG: {
      LIST: 'crm.tag.list',
      ADD: 'crm.tag.add',
      EDIT: 'crm.tag.edit',
      REMOVE: 'crm.tag.remove',
    },
  },
  FIN: {
    SUPPLIER: {
      LIST: 'fin.supplier.list',
      GET: 'fin.supplier.get',
      ADD: 'fin.supplier.add',
      EDIT: 'fin.supplier.edit',
      REMOVE: 'fin.supplier.remove',
    },
    CONTRACT: { LIST: 'fin.contract.list', GET: 'fin.contract.get', ADD: 'fin.contract.add', EDIT: 'fin.contract.edit', REMOVE: 'fin.contract.remove', APPROVE: 'fin.contract.approve' },
    EXPENSE: { LIST: 'fin.expense.list', GET: 'fin.expense.get', ADD: 'fin.expense.add', EDIT: 'fin.expense.edit', REMOVE: 'fin.expense.remove', APPROVE: 'fin.expense.approve' },
    INVOICE: { LIST: 'fin.invoice.list', GET: 'fin.invoice.get', ADD: 'fin.invoice.add', EDIT: 'fin.invoice.edit', REMOVE: 'fin.invoice.remove' },
    RECEIVABLE: { LIST: 'fin.receivable.list', GET: 'fin.receivable.get', ADD: 'fin.receivable.add', EDIT: 'fin.receivable.edit', REMOVE: 'fin.receivable.remove' },
    PAYABLE: { LIST: 'fin.payable.list', GET: 'fin.payable.get', ADD: 'fin.payable.add', EDIT: 'fin.payable.edit', REMOVE: 'fin.payable.remove' },
    FUND: { LIST: 'fin.fund.list', GET: 'fin.fund.get', ADD: 'fin.fund.add', EDIT: 'fin.fund.edit', REMOVE: 'fin.fund.remove' },
    PAYMENT: { LIST: 'fin.payment.list', GET: 'fin.payment.get', ADD: 'fin.payment.add', EDIT: 'fin.payment.edit', REMOVE: 'fin.payment.remove' },
    LOAN: { LIST: 'fin.loan.list', GET: 'fin.loan.get', ADD: 'fin.loan.add', EDIT: 'fin.loan.edit', REMOVE: 'fin.loan.remove', APPROVE: 'fin.loan.approve' },
    PORTAL: { CONFIG: 'fin.portal.config' },
  },
  HR: {
    EMPLOYEE: { LIST: 'hr.employee.list', GET: 'hr.employee.get', ADD: 'hr.employee.add', EDIT: 'hr.employee.edit', REMOVE: 'hr.employee.remove' },
    LEAVE: { LIST: 'hr.leave.list', GET: 'hr.leave.get', ADD: 'hr.leave.add', EDIT: 'hr.leave.edit', REMOVE: 'hr.leave.remove', APPROVE: 'hr.leave.approve' },
    RESIGN: { LIST: 'hr.resign.list', GET: 'hr.resign.get', ADD: 'hr.resign.add', EDIT: 'hr.resign.edit', REMOVE: 'hr.resign.remove', APPROVE: 'hr.resign.approve' },
    SALARY: { LIST: 'hr.salary.list', GET: 'hr.salary.get', ADD: 'hr.salary.add', EDIT: 'hr.salary.edit', REMOVE: 'hr.salary.remove', APPROVE: 'hr.salary.approve', CONFIRM: 'hr.salary.confirm' },
    CONTRACT: { LIST: 'hr.contract.list', GET: 'hr.contract.get', ADD: 'hr.contract.add', EDIT: 'hr.contract.edit', REMOVE: 'hr.contract.remove' },
  },
  SYS: {
    ANALYTICS: { VIEW: 'sys.analytics.view' },
    USER: {
      LIST: 'sys.user.list',
      GET: 'sys.user.get',
      ADD: 'sys.user.add',
      EDIT: 'sys.user.edit',
      REMOVE: 'sys.user.remove',
      RESET_PASSWORD: 'sys.user.reset-password',
    },
    ROLE: { LIST: 'sys.role.list', GET: 'sys.role.get', ADD: 'sys.role.add', EDIT: 'sys.role.edit', REMOVE: 'sys.role.remove' },
    DEPARTMENT: { LIST: 'sys.department.list', GET: 'sys.department.get', ADD: 'sys.department.add', EDIT: 'sys.department.edit', REMOVE: 'sys.department.remove' },
    DICT: { LIST: 'sys.dict.list', GET: 'sys.dict.get', ADD: 'sys.dict.add', EDIT: 'sys.dict.edit', REMOVE: 'sys.dict.remove' },
    LOG: { LIST: 'sys.log.list', GET: 'sys.log.get', REMOVE: 'sys.log.remove' },
    PERMISSION: { LIST: 'sys.permission.list' },
    MENU: { LIST: 'sys.menu.list', GET: 'sys.menu.get', ADD: 'sys.menu.add', EDIT: 'sys.menu.edit', REMOVE: 'sys.menu.remove' },
    DATA_PERMISSION: { LIST: 'sys.datapermission.list', EDIT: 'sys.datapermission.edit' },
    DIAGNOSIS: { VIEW: 'sys.diagnosis.view', RUN: 'sys.diagnosis.run' },
    LICENSE: { VIEW: 'sys.license.view', ACTIVATE: 'sys.license.activate' },
    EXPORT_AUDIT: { MANAGE: 'export:audit:manage' },
    MONITOR: { VIEW: 'system:monitor:view', MANAGE: 'system:monitor:manage' },
  },
  SECURITY: {
    AUDIT: {
      LIST: 'security:audit:list',
      DETAIL: 'security:audit:detail',
      EXPORT: 'security:audit:export',
    },
    MASK: { VIEW: 'mask:strategy:view', MANAGE: 'mask:strategy:manage' },
  },
  KB: {
    CATEGORY: { LIST: 'kb.category.list', ADD: 'kb.category.add', EDIT: 'kb.category.edit', REMOVE: 'kb.category.remove' },
    ARTICLE: { LIST: 'kb.article.list', GET: 'kb.article.get', ADD: 'kb.article.add', EDIT: 'kb.article.edit', REMOVE: 'kb.article.remove' },
  },
  WORKFLOW: {
    FLOW: { LIST: 'workflow.flow.list', GET: 'workflow.flow.get', ADD: 'workflow.flow.add', APPROVE: 'workflow.flow.approve', CANCEL: 'workflow.flow.cancel' },
  },
  EXPERT: {
    EXTERNAL: { LIST: 'expert.external.list', GET: 'expert.external.get', ADD: 'expert.external.add', EDIT: 'expert.external.edit', REMOVE: 'expert.external.remove' },
    EVALUATION: { LIST: 'expert.evaluation.list', GET: 'expert.evaluation.get', ADD: 'expert.evaluation.add', EDIT: 'expert.evaluation.edit', REMOVE: 'expert.evaluation.remove' },
    RECOMMEND: { LIST: 'expert.recommend.list', GET: 'expert.recommend.get', ADD: 'expert.recommend.add' },
  },
  WECOM: {
    INTEGRATION: { CONFIG: 'wecom.integration.config', SEND: 'wecom.integration.send' },
  },
} as const;
